/* Marketing header + footer shared by the home and industry pages of every GCC country. On a home page section links
 * scroll in place; elsewhere they go back to that country's home. Sign in and Start free trial are on every page, at
 * every width, and carry the page's country (and business category on an industry page). The country switcher
 * opens the same page in another country. */
import { useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { INDUSTRIES } from "../../../domain/industry";
import { useI18n } from "../../../i18n";
import { Icon, IconButton } from "../../../ui";
import { inCountry, loginRoute, signupRoute, stripCountry, useMktCountry } from "../mktCountry";
import { GCC, inName, isSaudi } from "../mktLocal";
import { LangSwitch, MktLink } from "./MktLink";

type Bi = [string, string];
export function scrollToId(id: string) {
  const el = document.getElementById(id);
  el?.scrollIntoView?.({ behavior: "smooth", block: "start" });
}

/** Section link: scrolls on the home page, otherwise a router link back to the country's home. */
function SecLink({ id, children, onHome }: { id: string; children: string; onHome: boolean }) {
  const c = useMktCountry();
  if (!onHome) return <MktLink to={inCountry("/home", c)}>{children}</MktLink>;
  return <a href={"#" + inCountry("/home", c)} onClick={(e) => { e.preventDefault(); scrollToId(id); }}>{children}</a>;
}

/** Country switcher: the same marketing page in each GCC country (a native disclosure, so it works before the app
 * has loaded on a prerendered page). */
function CountrySwitch() {
  const { L } = useI18n();
  const c = useMktCountry();
  const { pathname } = useLocation();
  const ref = useRef<HTMLDetailsElement>(null);
  const page = stripCountry(pathname);
  useEffect(() => {
    const close = (e: Event) => {
      const d = ref.current;
      if (d?.open && !(e instanceof KeyboardEvent ? e.key !== "Escape" : d.contains(e.target as Node))) d.open = false;
    };
    document.addEventListener("click", close);
    document.addEventListener("keydown", close);
    return () => { document.removeEventListener("click", close); document.removeEventListener("keydown", close); };
  }, []);
  return (
    <details className="mkt-cc" ref={ref} key={pathname} data-testid="country-switch">
      <summary className="ibtn mkt-cc-btn" aria-label={L(`Country: ${c.short[0]}. Change country`, `الدولة: ${c.short[1]}. تغيير الدولة`)} title={L("Change country", "تغيير الدولة")} data-testid="country-switch-btn">
        <span aria-hidden="true">{c.flag}</span><span className="mkt-cc-code">{c.code}</span>
      </summary>
      <div className="mkt-cc-menu" role="list" aria-label={L("GCC countries", "دول الخليج")}>
        {GCC.map((x) => (
          <span role="listitem" key={x.code}>
            <MktLink to={inCountry(page, x)} className={"mkt-cc-item" + (x.code === c.code ? " on" : "")} data-testid={"cc-" + x.code.toLowerCase()}>
              <span aria-hidden="true">{x.flag}</span><span>{L(...x.short)}</span>{x.code === c.code && <Icon name="check" className="s" />}
            </MktLink>
          </span>
        ))}
      </div>
    </details>
  );
}

/** Button text with a shorter form for phones (one span when both are the same). */
function BtnLabel({ long, short }: { long: string; short: string }) {
  if (long === short) return <span>{long}</span>;
  return <><span className="lbl-l">{long}</span><span className="lbl-s" aria-hidden="true">{short}</span></>;
}

export function MktHeader({ links, onHome, category }: { links: [string, Bi][]; onHome: boolean; category?: string }) {
  const i = useI18n();
  const { L, t } = i;
  const c = useMktCountry();
  return (
    <header className="mkt-nav dark">
      <div className="mkt-in row nw between">
        <MktLink to={inCountry("/home", c)} className="row nw mkt-logo"><span className="brand-mark">{L("S", "س")}</span><b>{L("StartHR", "ستارت HR")}</b></MktLink>
        <nav className="mkt-links" aria-label={L("Sections", "الأقسام")}>
          {links.map(([id, l]) => <SecLink key={id} id={id} onHome={onHome}>{L(...l)}</SecLink>)}
          <MktLink to={inCountry("/industries", c)} data-testid="nav-industries">{L("Industries", "القطاعات")}</MktLink>
          <MktLink to="/pricing">{L("Plans", "الباقات")}</MktLink>
        </nav>
        <div className="row nw mkt-acts">
          <CountrySwitch />
          <LangSwitch className="ibtn langbtn" label={i.isAr ? "Switch to English" : t("Switch to Arabic")}>{L("ع", "EN")}</LangSwitch>
          <IconButton icon={i.isDark ? "sun" : "moon"} label={i.isDark ? t("Light mode") : t("Dark mode")} onClick={i.toggleTheme} data-testid="theme-toggle" />
          <Link className="btn ghost sm on-dark" to={loginRoute(c, category)} aria-label={t("Sign in")} data-testid="home-login"><BtnLabel long={t("Sign in")} short={L("Sign in", "دخول")} /></Link>
          <Link className="btn gold sm" to={signupRoute(c, category)} aria-label={L("Start free trial", "ابدأ مجانًا")} data-testid="home-trial"><BtnLabel long={L("Start free trial", "ابدأ مجانًا")} short={L("Sign up", "سجّل")} /></Link>
        </div>
      </div>
    </header>
  );
}

export function MktFooter({ onHome }: { onHome: boolean }) {
  const { L, t } = useI18n();
  const c = useMktCountry();
  const the = inName(c);
  const cols: [string, [string, string][]][] = [
    [L("Product", "المنتج"), [[L("Payroll", "الرواتب"), "features"], [L("Compliance", "الامتثال"), "compliance"], [L("AI automation", "الأتمتة الذكية"), "ai"], [L("Biometric devices", "أجهزة البصمة"), "devices"]]],
    [L("Industries", "القطاعات"), [...INDUSTRIES.map((x): [string, string] => [L(...x.name), inCountry("/industries/" + x.key, c)]), [L("All industries", "كل القطاعات"), inCountry("/industries", c)]]],
    [L("Company", "الشركة"), [[L("Pricing", "الأسعار"), "/pricing"], [L("Sign in", "تسجيل الدخول"), loginRoute(c)], [L("Start free trial", "ابدأ مجانًا"), signupRoute(c)]]],
    [L("Legal", "قانوني"), [[L("Privacy policy", "سياسة الخصوصية"), "security"], [L("Terms of service", "شروط الخدمة"), "faq"], [L("Data processing", "معالجة البيانات"), "security"]]],
  ];
  return (
    <footer className="mkt-foot">
      <div className="mkt-in foot-g foot-g5">
        <div>
          <div className="row nw"><span className="brand-mark">{L("S", "س")}</span><b>{L("StartHR", "ستارت HR")}</b></div>
          <p className="sm muted">{isSaudi(c) ? L("HR and payroll for Saudi businesses.", "الموارد البشرية والرواتب للمنشآت السعودية.") : L(`HR and payroll for businesses in ${the[0]}.`, `الموارد البشرية والرواتب للمنشآت في ${the[1]}.`)}</p>
          <span className="chip ok"><Icon name="shield" className="s" />{isSaudi(c) ? <>{t("Data hosted in KSA")} · PDPL</> : L(...c.hosting)}</span>
        </div>
        {cols.map((col) => (
          <div key={col[0]} data-testid={col[1][0][1].includes("/industries") ? "foot-industries" : undefined}>
            <b className="sm">{col[0]}</b>
            <ul>{col[1].map(([x, to]) => <li key={x}>{to.startsWith("/") ? <MktLink to={to}>{x}</MktLink> : <SecLink id={to} onHome={onHome}>{x}</SecLink>}</li>)}</ul>
          </div>
        ))}
      </div>
      <nav className="mkt-in foot-cc" aria-label={L("StartHR in the GCC", "ستارت HR في دول الخليج")} data-testid="foot-countries">
        <b className="sm">{L("StartHR in", "ستارت HR في")}</b>
        {GCC.map((x) => <MktLink key={x.code} to={inCountry("/home", x)} data-testid={"foot-cc-" + x.code.toLowerCase()}><span aria-hidden="true">{x.flag}</span> {L(...x.short)}</MktLink>)}
      </nav>
      <div className="mkt-in foot-b xs muted">© 2026 {L("StartHR · Prototype for demonstration", "ستارت HR · نموذج أولي للعرض")}</div>
    </footer>
  );
}
